import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemedToaster } from "./lib/theme";
import { AuthProvider, LangProvider, useAuth } from "./lib/context";
import Shell, { Loading } from "./components/Shell";
import Login, { AuthCallback } from "./pages/Login";
import Home, { StartJourney } from "./pages/Home";
import Wizard from "./pages/Wizard";
import AppStatus, { ResumeTrack } from "./pages/AppStatus";
import MerchantDashboard from "./pages/MerchantDashboard";
import AdminQueues, { AdminReview } from "./pages/admin/AdminQueues";
import { AdminIntegrations, AdminConfig, AdminAudit } from "./pages/admin/AdminPages";
import { SiteLayout } from "./sx/site/Site";
import Home3D from "./sx/site/Home3D";
import { ProductsIndex, ProductPage } from "./sx/site/Products";
import { SolutionsIndex, SolutionPage, SegmentsIndex, SegmentPage, AboutPage } from "./sx/site/Solutions";
import { PlatformPage, UpiPage, RoutingPage, ReconPage, IntegrationsPage, DevelopersPage, SecurityPage, ContactPage } from "./sx/site/Pages";
import DashLayout from "./sx/dash/Layout";
import Overview from "./sx/dash/Overview";
import { Transactions, Routing, Connections } from "./sx/dash/Ops";
import { Reconciliation, Settlements, Alerts, ApiMonitoring, Webhooks } from "./sx/dash/Ops2";
import { Sandbox, Reports, AuditLogs, UsersPage, SettingsPage, Profile, ForgotPassword, ResetPassword } from "./sx/dash/Ops3";
import { OnbCases, OnbCase, Approvals, Products, UatCentre } from "./sx/dash/Onb";
import PartnerReadiness from "./sx/dash/P9";

export const homeFor = (u) => (u.permissions.includes("*") || u.permissions.includes("sx.view") ? "/dashboard" : u.is_staff && u.role !== "field_agent" ? "/admin" : "/onboarding");

function Protected({ children, staff }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (staff && !user.is_staff) return <Navigate to="/onboarding" replace />;
  return <Shell>{children}</Shell>;
}

function AppGate() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(user)} replace />;
}

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<SiteLayout />}>
        <Route path="/" element={<Home3D />} />
        <Route path="/products" element={<ProductsIndex />} />
        <Route path="/products/:slug" element={<ProductPage />} />
        <Route path="/platform" element={<PlatformPage />} />
        <Route path="/platform/upi-switch" element={<UpiPage />} />
        <Route path="/platform/routing" element={<RoutingPage />} />
        <Route path="/platform/reconciliation" element={<ReconPage />} />
        <Route path="/solutions" element={<SolutionsIndex />} />
        <Route path="/solutions/:slug" element={<SolutionPage />} />
        <Route path="/who-we-serve" element={<SegmentsIndex />} />
        <Route path="/who-we-serve/:slug" element={<SegmentPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/developers" element={<DevelopersPage />} />
        <Route path="/security" element={<SecurityPage />} />
        <Route path="/company" element={<AboutPage />} />
        <Route path="/about-us" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
      </Route>
      <Route path="/login" element={user ? <Navigate to="/app" replace /> : <Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/app" element={<AppGate />} />
      <Route path="/dashboard" element={<DashLayout />}>
        <Route index element={<Overview />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="routing" element={<Routing />} />
        <Route path="connections" element={<Connections />} />
        <Route path="reconciliation" element={<Reconciliation />} />
        <Route path="settlements" element={<Settlements />} />
        <Route path="alerts" element={<Alerts />} />
        <Route path="api-monitoring" element={<ApiMonitoring />} />
        <Route path="webhooks" element={<Webhooks />} />
        <Route path="reports" element={<Reports />} />
        <Route path="audit" element={<AuditLogs />} />
        <Route path="sandbox" element={<Sandbox />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<Profile />} />
        <Route path="onboarding" element={<OnbCases />} />
        <Route path="onboarding/:id" element={<OnbCase />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="products" element={<Products />} />
        <Route path="uat" element={<UatCentre />} />
        <Route path="integration" element={<PartnerReadiness />} />
      </Route>
      <Route path="/onboarding" element={<Protected><Home /></Protected>} />
      <Route path="/start/:kind" element={<Protected><StartJourney /></Protected>} />
      <Route path="/applications/:id" element={<Protected><Wizard /></Protected>} />
      <Route path="/applications/:id/status" element={<Protected><AppStatus /></Protected>} />
      <Route path="/resume" element={<Protected><ResumeTrack /></Protected>} />
      <Route path="/merchant" element={<Protected><MerchantDashboard /></Protected>} />
      <Route path="/admin" element={<Protected staff><AdminQueues /></Protected>} />
      <Route path="/admin/applications/:id" element={<Protected staff><AdminReview /></Protected>} />
      <Route path="/admin/integrations" element={<Protected staff><AdminIntegrations /></Protected>} />
      <Route path="/admin/config" element={<Protected staff><AdminConfig /></Protected>} />
      <Route path="/admin/audit" element={<Protected staff><AdminAudit /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <ThemedToaster richColors position="bottom-right" />
      </AuthProvider>
    </LangProvider>
  );
}
